import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocalOffer as CouponIcon,
  Money as PaymentIcon
} from '@mui/icons-material';
import { bookingService, authService, hotelService } from '../../services';

// Booking status options
const BOOKING_STATUSES = [
  'pending',
  'confirmed',
  'checked_in',
  'checked_out',
  'cancelled',
  'no_show'
];

// Payment status options
const PAYMENT_STATUSES = [
  'pending',
  'partial',
  'completed',
  'refunded',
  'failed'
];

const BookingManagement = () => {
  // State for bookings data
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for hotels filter
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');

  // State for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('view'); // 'view', 'edit', 'coupon', 'payment'
  const [currentBooking, setCurrentBooking] = useState(null);

  // Form state for editing
  const [bookingStatus, setBookingStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  // Check if user has permission
  const isAdmin = authService.hasRole('admin');
  const isManager = authService.hasRole('manager');
  const hasAccess = isAdmin || isManager;

  // Fetch bookings on component mount
  useEffect(() => {
    if (!hasAccess) {
      setError('You do not have permission to access this page.');
      return;
    }

    fetchHotels();
    // fetchBookings is now called when selectedHotel changes (or initially when it is empty)
  }, [hasAccess]);

  // Dependency on selectedHotel to refetch when filter changes
  useEffect(() => {
    if (hasAccess) {
      fetchBookings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotel, hasAccess]);

  const fetchHotels = async () => {
    try {
      const hotelsData = await hotelService.fetchAllHotels();
      setHotels(hotelsData);
    } catch (err) {
      console.error('Error fetching hotels:', err);
    }
  };

  // Fetch all bookings
  const fetchBookings = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = selectedHotel ? { hotelId: selectedHotel } : {};
      const bookingsData = await bookingService.fetchAllBookings(filters);
      setBookings(bookingsData);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open dialog for viewing booking details
  const handleOpenViewDialog = (booking) => {
    setDialogMode('view');
    setCurrentBooking(booking);
    setOpenDialog(true);
  };

  // Open dialog for editing booking status
  const handleOpenEditDialog = (booking) => {
    setDialogMode('edit');
    setCurrentBooking(booking);
    setBookingStatus(booking.status || 'pending');
    setPaymentStatus(booking.paymentStatus || 'pending');
    setOpenDialog(true);
  };

  // Open dialog for applying coupon
  const handleOpenCouponDialog = (booking) => {
    setDialogMode('coupon');
    setCurrentBooking(booking);
    setCouponCode(booking.couponCode || '');
    setOpenDialog(true);
  };

  // Open dialog for updating payment
  const handleOpenPaymentDialog = (booking) => {
    setDialogMode('payment');
    setCurrentBooking(booking);
    setPaymentAmount('');
    setPaymentMethod('');
    setPaymentReference('');
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle status update
  const handleUpdateStatus = async () => {
    if (!currentBooking || !bookingStatus) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await bookingService.updateBookingStatus(currentBooking.id, bookingStatus);
      // Refresh bookings list
      await fetchBookings();
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError(err.response?.data?.message || 'Failed to update booking status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!currentBooking || !couponCode) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await bookingService.applyBookingCoupon(currentBooking.id, couponCode);
      // Refresh bookings list
      await fetchBookings();
      handleCloseDialog();
    } catch (err) {
      console.error('Error applying coupon:', err);
      setError(err.response?.data?.message || 'Failed to apply coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = async () => {
    if (!currentBooking || !currentBooking.couponCode) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await bookingService.removeBookingCoupon(currentBooking.id);
      // Refresh bookings list
      await fetchBookings();
      handleCloseDialog();
    } catch (err) {
      console.error('Error removing coupon:', err);
      setError(err.response?.data?.message || 'Failed to remove coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment update
  const handleUpdatePayment = async () => {
    if (!currentBooking || !paymentAmount || !paymentMethod) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await bookingService.updateBookingPayment(currentBooking.id, {
        amount: parseFloat(paymentAmount),
        method: paymentMethod,
        reference: paymentReference || undefined
      });
      // Refresh bookings list
      await fetchBookings();
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating payment:', err);
      setError(err.response?.data?.message || 'Failed to update payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle booking deletion
  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await bookingService.deleteBooking(bookingId);
      // Refresh bookings list
      await fetchBookings();
    } catch (err) {
      console.error('Error deleting booking:', err);
      setError(err.response?.data?.message || 'Failed to delete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get status color based on booking status
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'checked_in':
        return 'info';
      case 'checked_out':
        return 'secondary';
      case 'cancelled':
      case 'no_show':
        return 'error';
      default:
        return 'warning';
    }
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'partial':
        return 'warning';
      case 'refunded':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate total nights
  const calculateNights = (checkIn, checkOut) => {
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // If not admin or manager, show access denied
  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You must be an admin or manager to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Booking Management
        </Typography>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="hotel-filter-label">Filter by Hotel</InputLabel>
          <Select
            labelId="hotel-filter-label"
            id="hotel-filter"
            value={selectedHotel}
            label="Filter by Hotel"
            onChange={(e) => setSelectedHotel(e.target.value)}
          >
            <MenuItem value="">
              <em>All Hotels</em>
            </MenuItem>
            {hotels.map((hotel) => (
              <MenuItem key={hotel.id} value={hotel.id}>
                {hotel.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Check In/Out</TableCell>
                <TableCell>Room Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Total</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No bookings found.
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.referenceNumber}</TableCell>
                    <TableCell>{`${booking.guestFirstName} ${booking.guestLastName}`}</TableCell>
                    <TableCell>
                      {formatDate(booking.checkInDate)} - {formatDate(booking.checkOutDate)}
                      <br />
                      <Typography variant="caption">
                        {calculateNights(booking.checkInDate, booking.checkOutDate)} nights
                      </Typography>
                    </TableCell>
                    <TableCell>{booking.roomType?.name || 'N/A'}<br />
                      <Typography variant="caption" color="text.secondary">
                        {booking.hotel?.name || 'Hotel'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status || 'pending'}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.paymentStatus || 'pending'}
                        color={getPaymentStatusColor(booking.paymentStatus)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      ₹{booking.totalAmount}
                      {booking.couponCode && (
                        <Tooltip title={`Coupon: ${booking.couponCode}`}>
                          <CouponIcon fontSize="small" color="secondary" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenViewDialog(booking)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(booking)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Apply Coupon">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleOpenCouponDialog(booking)}
                        >
                          <CouponIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Payment">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleOpenPaymentDialog(booking)}
                        >
                          <PaymentIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Booking">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBooking(booking.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Booking Dialog */}
      {dialogMode === 'view' && currentBooking && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Booking Details - {currentBooking.referenceNumber}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Guest Information</Typography>
                <Typography>
                  Name: {currentBooking.guestFirstName} {currentBooking.guestLastName}
                </Typography>
                <Typography>
                  Email: {currentBooking.guestEmail}
                </Typography>
                <Typography>
                  Phone: {currentBooking.guestPhone || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Booking Information</Typography>
                <Typography>
                  Status: <Chip
                    label={currentBooking.status || 'pending'}
                    color={getStatusColor(currentBooking.status)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography>
                  Created: {formatDate(currentBooking.createdAt)}
                </Typography>
                <Typography>
                  Last Updated: {formatDate(currentBooking.updatedAt)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Stay Details</Typography>
                <Typography>
                  Check-in: {formatDate(currentBooking.checkInDate)}
                </Typography>
                <Typography>
                  Check-out: {formatDate(currentBooking.checkOutDate)}
                </Typography>
                <Typography>
                  Duration: {calculateNights(currentBooking.checkInDate, currentBooking.checkOutDate)} nights
                </Typography>
                <Typography>
                  Room Type: {currentBooking.roomType?.name || 'N/A'}
                </Typography>
                <Typography>
                  Guests: {currentBooking.numberOfGuests || 1} {currentBooking.numberOfGuests === 1 ? 'person' : 'people'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">Payment Details</Typography>
                <Typography>
                  Status: <Chip
                    label={currentBooking.paymentStatus || 'pending'}
                    color={getPaymentStatusColor(currentBooking.paymentStatus)}
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography>
                  Room Rate: ₹{currentBooking.roomRate || 0}/night
                </Typography>
                {currentBooking.couponCode && (
                  <>
                    <Typography>
                      Original Amount: ₹{currentBooking.originalAmount || currentBooking.totalAmount}
                    </Typography>
                    <Typography>
                      Discount: ₹{(currentBooking.originalAmount || currentBooking.totalAmount) - currentBooking.totalAmount}
                      {' '}<Chip label={currentBooking.couponCode} size="small" color="secondary" />
                    </Typography>
                  </>
                )}
                <Typography fontWeight="bold">
                  Total Amount: ₹{currentBooking.totalAmount}
                </Typography>
              </Grid>

              {currentBooking.specialRequests && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold">Special Requests</Typography>
                  <Typography>
                    {currentBooking.specialRequests}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Close
            </Button>
            <Button
              onClick={() => handleOpenEditDialog(currentBooking)}
              variant="contained"
              color="primary"
            >
              Edit Status
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Status Dialog */}
      {dialogMode === 'edit' && currentBooking && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Update Booking Status - {currentBooking.referenceNumber}
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="booking-status-label">Booking Status</InputLabel>
                <Select
                  labelId="booking-status-label"
                  id="booking-status"
                  value={bookingStatus}
                  label="Booking Status"
                  onChange={(e) => setBookingStatus(e.target.value)}
                  disabled={loading}
                >
                  {BOOKING_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="payment-status-label">Payment Status</InputLabel>
                <Select
                  labelId="payment-status-label"
                  id="payment-status"
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  disabled={loading}
                >
                  {PAYMENT_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              variant="contained"
              disabled={loading || !bookingStatus}
            >
              {loading ? <CircularProgress size={24} /> : 'Update Status'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Coupon Dialog */}
      {dialogMode === 'coupon' && currentBooking && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentBooking.couponCode ? 'Update Coupon' : 'Apply Coupon'} - {currentBooking.referenceNumber}
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1 }}>
              {currentBooking.couponCode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Current coupon: <strong>{currentBooking.couponCode}</strong>
                </Alert>
              )}

              <TextField
                margin="normal"
                fullWidth
                id="couponCode"
                label="Coupon Code"
                name="couponCode"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                disabled={loading}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            {currentBooking.couponCode && (
              <Button
                onClick={handleRemoveCoupon}
                color="error"
                disabled={loading}
              >
                Remove Coupon
              </Button>
            )}
            <Button
              onClick={handleApplyCoupon}
              variant="contained"
              disabled={loading || !couponCode}
            >
              {loading ? <CircularProgress size={24} /> : 'Apply Coupon'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Payment Dialog */}
      {dialogMode === 'payment' && currentBooking && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Update Payment - {currentBooking.referenceNumber}
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Total Amount: ₹{currentBooking.totalAmount}
              </Alert>

              <TextField
                margin="normal"
                required
                fullWidth
                id="paymentAmount"
                label="Payment Amount"
                name="paymentAmount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: '₹',
                }}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel id="payment-method-label">Payment Method</InputLabel>
                <Select
                  labelId="payment-method-label"
                  id="payment-method"
                  value={paymentMethod}
                  label="Payment Method"
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Credit/Debit Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                margin="normal"
                fullWidth
                id="paymentReference"
                label="Payment Reference/Transaction ID"
                name="paymentReference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                disabled={loading}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePayment}
              variant="contained"
              disabled={loading || !paymentAmount || !paymentMethod}
            >
              {loading ? <CircularProgress size={24} /> : 'Update Payment'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default BookingManagement;
